// src/components/dashboard/candidate/journey-steps/UserInfoStep.tsx
import React, { useState } from 'react';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { LegalConsent } from '@/components/legal/LegalConsent';

interface UserInfoStepProps {
    formData: any;
    updateFormData: (data: any) => void;
    onGoogleSignup?: () => void; // NEW: Callback for Google signup
    /** #324 — set once a submit was attempted without consent, so the checkbox
        turns red instead of the form failing silently. */
    showConsentError?: boolean;
}

export const UserInfoStep: React.FC<UserInfoStepProps> = ({
                                                              formData,
                                                              updateFormData,
                                                              onGoogleSignup, // NEW
                                                              showConsentError
                                                          }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    const handlePasswordChange = (password: string) => {
        updateFormData({ password });

        // Basic password validation
        if (password.length > 0 && password.length < 8) {
            setPasswordError('Lösenordet måste vara minst 8 tecken långt');
        } else {
            setPasswordError('');
        }
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    return (
        <div className="space-y-6 py-2">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-2xl flex items-center justify-center">
                    <User className="w-8 h-8 text-[#1A4D4F]" />
                </div>
                <h3 className="text-xl font-bold mb-2">Skapa ditt konto</h3>
                <p className="text-gray-600 text-sm">
                    Sista steget! Fyll i dina kontouppgifter för att slutföra registreringen.
                </p>
            </div>

            {/* NEW: Google Signup Button (at top for better UX) */}
            {onGoogleSignup && (
                <>
                    <button
                        type="button"
                        onClick={onGoogleSignup}
                        className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all font-medium text-gray-700"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Fortsätt med Google
                    </button>

                    {/* Divider */}
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t-2 border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-white text-gray-500 font-medium">eller med e-post</span>
                        </div>
                    </div>
                </>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
                {/* First Name */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Förnamn <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.firstName || ''}
                            onChange={(e) => updateFormData({ firstName: e.target.value })}
                            placeholder="Ditt förnamn"
                            className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                        />
                    </div>
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Efternamn <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.lastName || ''}
                            onChange={(e) => updateFormData({ lastName: e.target.value })}
                            placeholder="Ditt efternamn"
                            className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                        />
                    </div>
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        E-postadress <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="email"
                            value={formData.email || ''}
                            onChange={(e) => updateFormData({ email: e.target.value })}
                            placeholder="din.email@exempel.se"
                            className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                        />
                    </div>
                    {formData.email && !validateEmail(formData.email) && (
                        <p className="text-sm text-red-500 mt-1">Ange en giltig e-postadress</p>
                    )}
                </div>

                {/* Password */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Lösenord <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password || ''}
                            onChange={(e) => handlePasswordChange(e.target.value)}
                            placeholder="Minst 8 tecken"
                            className="w-full pl-11 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            {showPassword ? (
                                <EyeOff className="w-5 h-5" />
                            ) : (
                                <Eye className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    {passwordError && (
                        <p className="text-sm text-red-500 mt-1">{passwordError}</p>
                    )}
                    {/* Was "Starkt lösenord" for anything 8 characters long, which
                        called "aaaaaaaa" strong. It reports the rule it actually
                        checked. */}
                    {formData.password && formData.password.length >= 8 && !passwordError && (
                        <p className="text-sm text-green-600 mt-1 font-medium">Tillräckligt långt</p>
                    )}
                </div>

                {/* No "Bekräfta lösenord" field, deliberately. A confirm field only
                    exists because the input is masked — and it does not catch the
                    common mistake, which is typing the same wrong thing twice. The
                    eye toggle above solves the real problem: the user reads back the
                    actual characters. Same call as nordai, so both products ask for
                    a password the same way. */}
            </div>

            {/* #324 — consent, directly above the create-account action so it is
                the last thing read before the account exists. Gates the Google
                button too (see ProfileJourneyModal.handleGoogleSignup): signing
                up with Google creates exactly the same account, so it cannot be
                the way round the terms. */}
            <LegalConsent
                checked={!!formData.acceptedTerms}
                onChange={(acceptedTerms) => updateFormData({ acceptedTerms })}
                invalid={!!showConsentError && !formData.acceptedTerms}
            />

            {/* Final encouragement */}
            <div className="p-6 bg-[#1A4D4F] text-[#f5f5f5] rounded-2xl">
                <h3 className="text-lg font-bold mb-2">Nästan klar!</h3>
                <p className="text-sm opacity-90">
                    När du slutför din profil börjar vi matcha dig med relevanta jobbmöjligheter. Spännande!
                </p>
            </div>
        </div>
    );
};